import React, { Component } from 'react';
import { Navigate } from 'react-router-dom';
import { urlMap, getAuthHeaders, getEffectiveRole, appNavigation } from '../../utils';
import Modal from '../components/Modal';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import TabsMenu from '../../components/TabsMenu';
import '../../assets/styles/AdminPanel.css';
import '../../assets/styles/PublisherPanel.css';

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatMinutesPerSession(totalSeconds, sessionCount) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const sessions = Math.max(0, Number(sessionCount) || 0);
  if (sessions <= 0) return '0m';
  const minutes = Math.round((seconds / 60) / sessions);
  return `${minutes}m`;
}

function formatTimeOfDay(minuteOfDay) {
  const safeMinute = Math.max(0, Math.min(1439, Number(minuteOfDay) || 0));
  const hour = Math.floor(safeMinute / 60);
  const minute = safeMinute % 60;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function journeyColor(index) {
  const palette = ['#0ea5e9', '#22c55e', '#f97316', '#eab308', '#14b8a6', '#f43f5e', '#6366f1', '#84cc16'];
  return palette[index % palette.length];
}

function pointY(point, height, pad) {
  const plotHeight = height - pad.top - pad.bottom;
  const timeOfDayMinute = Math.max(0, Math.min(1439, Number(point?.timeOfDayMinute ?? 0)));
  const yRatio = timeOfDayMinute / 1439;
  return pad.top + yRatio * plotHeight;
}

function buildJourneyPath(points, maxMinute, width, height, pad) {
  if (!Array.isArray(points) || points.length === 0) return '';

  const plotWidth = width - pad.left - pad.right;

  return points
    .map((point, idx) => {
      const minute = Math.max(0, Number(point?.minute || 0));
      const x = pad.left + ((minute / Math.max(1, maxMinute)) * plotWidth);
      const y = pointY(point, height, pad);
      return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function svgEscape(value) {
  return escapeHtml(value);
}

function extractImageUrlFromHtml(html) {
  const raw = String(html || '');
  const bannerMatch = raw.match(/<img[^>]*data-sc-banner=["']1["'][^>]*src=["']([^"']+)["'][^>]*>/i)
    || raw.match(/<img[^>]*src=["']([^"']+)["'][^>]*data-sc-banner=["']1["'][^>]*>/i);
  if (bannerMatch?.[1]) return bannerMatch[1];

  const altMatch = raw.match(/<img[^>]*alt=["']Newsletter excerpt["'][^>]*src=["']([^"']+)["'][^>]*>/i)
    || raw.match(/<img[^>]*src=["']([^"']+)["'][^>]*alt=["']Newsletter excerpt["'][^>]*>/i);
  if (altMatch?.[1]) return altMatch[1];

  const nonLogo = raw.match(/<img[^>]*src=["']([^"']+)["'][^>]*>/gi) || [];
  for (const tag of nonLogo) {
    const src = tag.match(/src=["']([^"']+)["']/i)?.[1] || '';
    const alt = tag.match(/alt=["']([^"']+)["']/i)?.[1] || '';
    if (/logo/i.test(alt)) continue;
    if (src) return src;
  }
  return '';
}

function extractImagePositionFromHtml(html) {
  const raw = String(html || '');
  const match = raw.match(/data-sc-image-position=["'](top|middle|bottom)["']/i);
  const pos = String(match?.[1] || '').toLowerCase();
  return ['top', 'middle', 'bottom'].includes(pos) ? pos : 'top';
}

const NEWSLETTER_SCHEDULE_TIMEZONES = [
  'UTC',
  'Europe/Oslo',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
];

export default class PublisherPanel extends Component {
  static NEWSLETTER_SEED_URL_STORAGE_KEY = 'publisher-newsletter-seed-url';

  state = {
    activeTab: 'newsletter',
    loading: false,
    result: null,
    runError: null,
    listLoading: true,
    listItems: [],
    listError: null,
    bucket: '',
    requestListLoading: true,
    requestListError: null,
    publisherRequests: [],
    approvingRequestIds: {},
    scaffoldReviewLoading: true,
    scaffoldReviewError: null,
    scaffoldReviewItems: [],
    reviewModalOpen: false,
    reviewModalLoading: false,
    reviewModalError: null,
    reviewModalScaffold: null,
    reviewComment: '',
    reviewActionLoading: false,
    journeyLoading: false,
    journeyError: null,
    journeyDays: 30,
    journeyTimeline: [],
    journeyLines: [],
    journeyEventAxis: [],
    journeyEventTypeCounts: [],
    journeyCompletionTrend: [],
    journeyWarningSignals: {
      verifyWithoutLogin: { count: 0, samples: [] },
      scaffoldWithoutWriterSession: { count: 0, samples: [] },
      completedWriterWithoutReflection: { count: 0, samples: [] },
    },
    journeyWritingSessions: {
      completedCount: 0,
      abandonedCount: 0,
      mobileSeconds: 0,
      webSeconds: 0,
    },
    journeyWritingByUser: [],
    newsletterBlogUrl: '',
    newsletterSeedUrlSavedGlow: false,
    newsletterGenerating: false,
    newsletterError: null,
    newsletterSubscribersLoading: false,
    newsletterSubscribersError: null,
    newsletterSubscribersExpanded: false,
    newsletterSubscribers: [],
    newsletterRecipients: [],
    newsletterRecipientsSelected: {},
    newsletterRecipientsModalOpen: false,
    newsletterDraftsLoading: false,
    newsletterDraftsError: null,
    newsletterDrafts: [],
    newsletterCreatingDraft: false,
    newsletterDeletingDraftId: null,
    newsletterOpeningDraftId: null,
    newsletterSelectedDraftId: null,
    newsletterSubject: '',
    newsletterPreviewText: '',
    newsletterBodyText: '',
    newsletterImageUrl: '',
    newsletterImagePosition: 'top',
    newsletterCtaLabel: '',
    newsletterCtaUrl: '',
    newsletterPreviewHtml: '',
    newsletterPreviewModalOpen: false,
    newsletterPreviewLoading: false,
    newsletterPreviewDevice: 'desktop',
    newsletterSaving: false,
    newsletterAutoSaving: false,
    newsletterDirty: false,
    newsletterSending: false,
    newsletterScheduling: false,
    newsletterUnscheduling: false,
    newsletterScheduleModalOpen: false,
    newsletterScheduleAt: '',
    newsletterScheduleTz: 'UTC',
    newsletterPreviewThemeTick: 0,
  };

  newsletterAutosaveTimer = null;
  newsletterPreviewTimer = null;
  themeObserver = null;
  newsletterSeedUrlGlowTimer = null;

  componentDidMount() {
    const tabFromHash = this.getTabFromHash();
    if (tabFromHash && tabFromHash !== this.state.activeTab) {
      this.setState({ activeTab: tabFromHash });
    }

    window.addEventListener('hashchange', this.handleHashChange);
    this.setHashForTab('newsletter');
    if ((tabFromHash || this.state.activeTab) === 'newsletter') {
      this.bootstrapNewsletterTab();
    }

    try {
      const rememberedSeedUrl = String(localStorage.getItem(PublisherPanel.NEWSLETTER_SEED_URL_STORAGE_KEY) || '').trim();
      if (rememberedSeedUrl && !this.state.newsletterBlogUrl) {
        this.setState({ newsletterBlogUrl: rememberedSeedUrl });
      }
    } catch {
      // Ignore localStorage errors (private mode, blocked storage, etc.).
    }

    if (typeof window !== 'undefined' && window.MutationObserver) {
      this.themeObserver = new MutationObserver(() => {
        this.setState((prev) => ({ newsletterPreviewThemeTick: Number(prev.newsletterPreviewThemeTick || 0) + 1 }));
      });
      this.themeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['data-theme'],
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.handleHashChange);
    if (this.newsletterAutosaveTimer) {
      clearTimeout(this.newsletterAutosaveTimer);
      this.newsletterAutosaveTimer = null;
    }
    if (this.newsletterPreviewTimer) {
      clearTimeout(this.newsletterPreviewTimer);
      this.newsletterPreviewTimer = null;
    }
    if (this.newsletterSeedUrlGlowTimer) {
      clearTimeout(this.newsletterSeedUrlGlowTimer);
      this.newsletterSeedUrlGlowTimer = null;
    }
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }
  }

  persistNewsletterSeedUrl = (urlValue) => {
    const value = String(urlValue || '').trim();
    try {
      if (value) {
        localStorage.setItem(PublisherPanel.NEWSLETTER_SEED_URL_STORAGE_KEY, value);
      } else {
        localStorage.removeItem(PublisherPanel.NEWSLETTER_SEED_URL_STORAGE_KEY);
      }
      this.setState({ newsletterSeedUrlSavedGlow: true });
      if (this.newsletterSeedUrlGlowTimer) clearTimeout(this.newsletterSeedUrlGlowTimer);
      this.newsletterSeedUrlGlowTimer = setTimeout(() => {
        this.setState({ newsletterSeedUrlSavedGlow: false });
      }, 900);
    } catch {
      // Ignore localStorage errors.
    }
  };

  handleNewsletterSeedUrlChange = (nextValue) => {
    const value = String(nextValue || '');
    this.setState({ newsletterBlogUrl: value }, () => {
      this.persistNewsletterSeedUrl(value);
    });
  };

  isDarkThemeActive = () => {
    if (typeof document === 'undefined') return false;
    return document.documentElement.getAttribute('data-theme') === 'dark';
  };

  getThemeAwareNewsletterPreviewHtml = (rawHtml, previewDevice = 'desktop') => {
    const html = String(rawHtml || '');
    if (!html) return '';

    const isMobilePreview = previewDevice === 'mobile';
    const previewUserAgent = isMobilePreview
      ? 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1'
      : 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36';
    const escapedUserAgent = previewUserAgent.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

    const isDark = this.isDarkThemeActive();
    const overrideCss = isDark
      ? [
        ':root { color-scheme: dark; }',
        'body, .wrapper { background: #0f1115 !important; }',
        '.container, .header { background: #161a20 !important; border-color: #242933 !important; }',
        '.brand, .content { color: #f5f7fb !important; }',
        '.muted, .footer { color: #9aa3b2 !important; }',
        'a { color: #a78bfa !important; }',
        '.btn { background: #8b5cf6 !important; color: #ffffff !important; }',
        '.code { background: #1f2130 !important; color: #f5f7fb !important; }',
        ...(isMobilePreview
          ? [
            'body { margin: 0 auto !important; max-width: 390px !important; min-height: 100vh; }',
            '.wrapper, .container, .header { width: 100% !important; max-width: 390px !important; margin-left: auto !important; margin-right: auto !important; }',
          ]
          : [
            'body { margin: 0 !important; max-width: none !important; }',
            '.wrapper, .container, .header { max-width: none !important; width: auto !important; }',
          ]),
      ].join(' ')
      : [
        ':root { color-scheme: light; }',
        'body, .wrapper { background: #f9f9f9 !important; }',
        '.container, .header { background: #ffffff !important; border-color: #eeeeee !important; }',
        '.brand, .content { color: #222222 !important; }',
        '.muted, .footer { color: #777777 !important; }',
        'a { color: #7c3aed !important; }',
        '.btn { background: #7c3aed !important; color: #ffffff !important; }',
        '.code { background: #f3f4f6 !important; color: #222222 !important; }',
        ...(isMobilePreview
          ? [
            'body { margin: 0 auto !important; max-width: 390px !important; min-height: 100vh; }',
            '.wrapper, .container, .header { width: 100% !important; max-width: 390px !important; margin-left: auto !important; margin-right: auto !important; }',
          ]
          : [
            'body { margin: 0 !important; max-width: none !important; }',
            '.wrapper, .container, .header { max-width: none !important; width: auto !important; }',
          ]),
      ].join(' ');

    const viewportMeta = isMobilePreview
      ? '<meta name="viewport" content="width=device-width, initial-scale=1" />'
      : '<meta name="viewport" content="width=1280, initial-scale=1" />';
    const userAgentScript = `<script>(function(){try{Object.defineProperty(window.navigator,'userAgent',{get:function(){return '${escapedUserAgent}';},configurable:true});}catch(_){}})();</script>`;
    const overrideStyle = `<style id="sc-preview-theme-override">${overrideCss}</style>`;
    const headInjection = `${viewportMeta}${userAgentScript}${overrideStyle}`;
    if (html.includes('</head>')) {
      return html.replace('</head>', `${headInjection}</head>`);
    }
    return `${headInjection}${html}`;
  };

  getTabFromHash = () => {
    const raw = String(window.location.hash || '').replace(/^#/, '').trim();
    const allowed = new Set(['newsletter']);
    return allowed.has(raw) ? raw : null;
  };

  setHashForTab = (tab) => {
    const nextHash = `#${tab}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, '', nextHash);
    }
  };

  handleHashChange = () => {
    const tabFromHash = this.getTabFromHash();
    if (tabFromHash && tabFromHash !== this.state.activeTab) {
      this.setState({ activeTab: tabFromHash });
      if (tabFromHash === 'newsletter') this.bootstrapNewsletterTab();
    }
  };

  handleTabChange = (tab) => {
    this.setState({ activeTab: tab });
    this.setHashForTab(tab);
    if (tab === 'newsletter') this.bootstrapNewsletterTab();
  };

  bootstrapNewsletterTab = async () => {
    if (this.state.newsletterSubscribers.length === 0) {
      this.fetchNewsletterSubscribers();
    }
    if (this.state.newsletterDrafts.length === 0) {
      this.fetchNewsletterDrafts();
    }
  };

  getNewsletterUrlMap = () => ({
    generate: urlMap.workerPublisherNewsletterGenerate,
    subscribers: urlMap.workerPublisherNewsletterSubscribers,
    drafts: urlMap.workerPublisherNewsletterDrafts,
    draft: urlMap.workerPublisherNewsletterDraft,
    deleteDraft: urlMap.workerPublisherNewsletterDeleteDraft,
    previewTemplate: urlMap.workerPublisherNewsletterPreviewTemplate,
    sendNow: urlMap.workerPublisherNewsletterSendNow,
    schedule: urlMap.workerPublisherNewsletterSchedule,
    unschedule: urlMap.workerPublisherNewsletterUnschedule,
  });

  fetchNewsletterSubscribers = async () => {
    this.setState({ newsletterSubscribersLoading: true, newsletterSubscribersError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.subscribers, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const recipients = Array.isArray(data.recipients)
        ? data.recipients
        : (Array.isArray(data.subscribers) ? data.subscribers.map((s) => ({
            email: s.email,
            source: 'newsletter',
            created_at: s.created_at,
          })) : []);
      this.setState((prev) => ({
        newsletterSubscribers: recipients,
        newsletterRecipients: recipients,
        newsletterRecipientsSelected: recipients.reduce((acc, item) => {
          const email = String(item?.email || '').trim().toLowerCase();
          if (!email) return acc;
          const prevSelected = prev.newsletterRecipientsSelected?.[email];
          acc[email] = typeof prevSelected === 'boolean' ? prevSelected : true;
          return acc;
        }, {}),
        newsletterSubscribersLoading: false,
      }));
    } catch (err) {
      this.setState({
        newsletterSubscribersError: err.message || 'Failed to load subscribers.',
        newsletterSubscribersLoading: false,
      });
    }
  };

  hydrateNewsletterDraft = (draft) => {
    if (!draft) return;
    this.setState({
      newsletterBlogUrl: draft.source_post_url || '',
      newsletterSelectedDraftId: draft.id,
      newsletterSubject: draft.subject || '',
      newsletterPreviewText: draft.preview_text || '',
      newsletterBodyText: draft.body_text || '',
      newsletterImageUrl: draft.source_image_url || extractImageUrlFromHtml(draft.body_html) || '',
      newsletterImagePosition: extractImagePositionFromHtml(draft.body_html),
      newsletterCtaLabel: draft.cta_primary_label || '',
      newsletterCtaUrl: draft.cta_primary_url || draft.source_post_url || '',
      newsletterPreviewHtml: draft.body_html || '',
      newsletterDirty: false,
    });
    this.queueNewsletterPreview();
  };

  resetNewsletterComposerForNewCampaign = () => {
    this.setState({
      newsletterBlogUrl: '',
      newsletterSelectedDraftId: null,
      newsletterSubject: '',
      newsletterPreviewText: '',
      newsletterBodyText: '',
      newsletterImageUrl: '',
      newsletterImagePosition: 'top',
      newsletterCtaLabel: '',
      newsletterCtaUrl: '',
      newsletterPreviewHtml: '',
      newsletterDirty: false,
      newsletterError: null,
      newsletterScheduleAt: '',
    });
    this.persistNewsletterSeedUrl('');
  };

  createNewsletterDraft = async () => {
    const fallbackSourcePostUrl = 'https://storyclub.ai';
    const sourcePostUrl = String(this.state.newsletterBlogUrl || '').trim() || fallbackSourcePostUrl;

    this.setState({ newsletterCreatingDraft: true, newsletterError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.drafts, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          sourcePostUrl,
          subject: '',
          previewText: '',
          bodyText: '',
          bodyHtml: '',
          ctaPrimaryLabel: '',
          ctaPrimaryUrl: '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      const createdDraftId = String(data?.draft?.id || '').trim() || null;
      await this.fetchNewsletterDrafts({ autoSelect: true, preferredDraftId: createdDraftId || undefined });

      if (!String(this.state.newsletterBlogUrl || '').trim()) {
        this.setState({ newsletterBlogUrl: '' });
      }

      this.setState({ newsletterCreatingDraft: false });
    } catch (err) {
      this.setState({
        newsletterCreatingDraft: false,
        newsletterError: err.message || 'Failed to create campaign.',
      });
    }
  };

  loadNewsletterDraftById = async (draftId) => {
    const id = String(draftId || '').trim();
    if (!id) return;
    this.setState({ newsletterOpeningDraftId: id, newsletterError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.draft(id), {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const draft = data?.draft || (this.state.newsletterDrafts || []).find((d) => d.id === id);
      if (!draft) throw new Error('Draft not found.');
      this.hydrateNewsletterDraft(draft);
      this.setState({ newsletterOpeningDraftId: null });
    } catch (err) {
      this.setState({
        newsletterOpeningDraftId: null,
        newsletterError: err.message || 'Failed to load campaign.',
      });
    }
  };

  fetchNewsletterDrafts = async (options = {}) => {
    const autoSelect = options?.autoSelect !== false;
    const preferredDraftId = String(options?.preferredDraftId || '').trim() || null;
    this.setState({ newsletterDraftsLoading: true, newsletterDraftsError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.drafts, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const drafts = Array.isArray(data.drafts) ? data.drafts : [];
      this.setState({ newsletterDrafts: drafts, newsletterDraftsLoading: false });
      if (autoSelect && drafts.length > 0) {
        const selected = drafts.find((d) => d.id === preferredDraftId)
          || drafts.find((d) => d.id === this.state.newsletterSelectedDraftId)
          || drafts[0];
        this.hydrateNewsletterDraft(selected);
      }
    } catch (err) {
      this.setState({
        newsletterDraftsError: err.message || 'Failed to load drafts.',
        newsletterDraftsLoading: false,
      });
    }
  };

  updateNewsletterField = (key, value) => {
    this.setState({ [key]: value, newsletterDirty: true }, () => {
      this.queueNewsletterAutosave();
      this.queueNewsletterPreview();
    });
  };

  queueNewsletterPreview = () => {
    if (this.newsletterPreviewTimer) {
      clearTimeout(this.newsletterPreviewTimer);
    }

    this.newsletterPreviewTimer = setTimeout(() => {
      this.refreshNewsletterPreview();
    }, 320);
  };

  refreshNewsletterPreview = async () => {
    if (!this.state.newsletterSelectedDraftId && !String(this.state.newsletterBodyText || '').trim()) return;

    this.setState({ newsletterPreviewLoading: true });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.previewTemplate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          subject: this.state.newsletterSubject,
          previewText: this.state.newsletterPreviewText,
          bodyText: this.state.newsletterBodyText,
          imageUrl: this.state.newsletterImageUrl,
          imagePosition: this.state.newsletterImagePosition,
          ctaLabel: this.state.newsletterCtaLabel,
          ctaUrl: this.state.newsletterCtaUrl,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this.setState({
        newsletterPreviewHtml: String(data.html || ''),
        newsletterPreviewLoading: false,
      });
    } catch (err) {
      this.setState({ newsletterPreviewLoading: false });
    }
  };

  queueNewsletterAutosave = () => {
    const { newsletterSelectedDraftId } = this.state;
    if (!newsletterSelectedDraftId) return;

    if (this.newsletterAutosaveTimer) {
      clearTimeout(this.newsletterAutosaveTimer);
    }

    this.newsletterAutosaveTimer = setTimeout(() => {
      this.persistNewsletterDraft('autosave');
    }, 900);
  };

  persistNewsletterDraft = async (mode = 'manual') => {
    const draftId = this.state.newsletterSelectedDraftId;
    if (!draftId) {
      this.setState({ newsletterError: 'Select a draft first.' });
      return;
    }

    const isManual = mode === 'manual';
    this.setState({
      newsletterError: null,
      newsletterSaving: isManual,
      newsletterAutoSaving: !isManual,
    });

    try {
      const payload = {
        subject: this.state.newsletterSubject,
        previewText: this.state.newsletterPreviewText,
        bodyText: this.state.newsletterBodyText,
        imageUrl: this.state.newsletterImageUrl,
        imagePosition: this.state.newsletterImagePosition,
        ctaLabel: this.state.newsletterCtaLabel,
        ctaUrl: this.state.newsletterCtaUrl,
      };

      // Persist draft content even if preview rendering fails.
      let renderedHtml = String(this.state.newsletterPreviewHtml || '');
      try {
        const urls = this.getNewsletterUrlMap();
        const previewRes = await fetch(urls.previewTemplate, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify(payload),
        });
        const previewData = await previewRes.json().catch(() => ({}));
        if (previewRes.ok) {
          renderedHtml = String(previewData.html || renderedHtml || '');
        }
      } catch {
        // Ignore preview-render errors here; draft text still needs to be saved.
      }

      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.draft(draftId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          sourcePostUrl: this.state.newsletterBlogUrl,
          subject: payload.subject,
          previewText: payload.previewText,
          bodyText: payload.bodyText,
          bodyHtml: renderedHtml,
          ctaPrimaryLabel: payload.ctaLabel,
          ctaPrimaryUrl: payload.ctaUrl,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      const updated = data?.draft;
      if (updated?.id) {
        this.setState((prev) => ({
          newsletterDrafts: (prev.newsletterDrafts || []).map((d) => (d.id === updated.id ? { ...d, ...updated } : d)),
          newsletterPreviewHtml: renderedHtml,
          newsletterDirty: false,
          newsletterSaving: false,
          newsletterAutoSaving: false,
        }));
      } else {
        this.setState({ newsletterPreviewHtml: renderedHtml, newsletterDirty: false, newsletterSaving: false, newsletterAutoSaving: false });
      }

      if (isManual) {
        await this.fetchNewsletterDrafts();
      }
    } catch (err) {
      this.setState({
        newsletterError: err.message || 'Failed to save draft.',
        newsletterSaving: false,
        newsletterAutoSaving: false,
      });
    }
  };

  generateNewsletterDraft = async () => {
    const blogUrl = String(this.state.newsletterBlogUrl || '').trim();
    if (!blogUrl) {
      this.setState({ newsletterError: 'Please provide a blog URL first.' });
      return;
    }

    this.setState({ newsletterGenerating: true, newsletterError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.generate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ blogUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const preferredDraftId = String(data?.draft?.id || '').trim() || null;
      await this.fetchNewsletterDrafts({ autoSelect: true, preferredDraftId });
      this.setState({ newsletterGenerating: false });
    } catch (err) {
      this.setState({
        newsletterError: err.message || 'Failed to generate draft.',
        newsletterGenerating: false,
      });
    }
  };

  saveNewsletterDraft = async () => {
    await this.persistNewsletterDraft('manual');
  };

  openNewsletterRecipientsModal = () => {
    this.setState({ newsletterRecipientsModalOpen: true });
    if (!this.state.newsletterRecipients.length) {
      this.fetchNewsletterSubscribers();
    }
  };

  closeNewsletterRecipientsModal = () => {
    this.setState({ newsletterRecipientsModalOpen: false });
  };

  setNewsletterRecipientSelected = (email, checked) => {
    const key = String(email || '').trim().toLowerCase();
    if (!key) return;
    this.setState((prev) => ({
      newsletterRecipientsSelected: {
        ...prev.newsletterRecipientsSelected,
        [key]: Boolean(checked),
      },
    }));
  };

  toggleNewsletterRecipient = (email) => {
    const key = String(email || '').trim().toLowerCase();
    if (!key) return;
    const isChecked = this.state.newsletterRecipientsSelected[key] !== false;
    this.setNewsletterRecipientSelected(key, !isChecked);
  };

  setAllNewsletterRecipients = (checked) => {
    this.setState((prev) => ({
      newsletterRecipientsSelected: (prev.newsletterRecipients || []).reduce((acc, item) => {
        const key = String(item?.email || '').trim().toLowerCase();
        if (key) acc[key] = Boolean(checked);
        return acc;
      }, {}),
    }));
  };

  setAllNewsletterSubscribersSelected = (checked) => {
    this.setState((prev) => {
      const nextSelected = { ...prev.newsletterRecipientsSelected };
      (prev.newsletterSubscribers || []).forEach((item) => {
        const key = String(item?.email || '').trim().toLowerCase();
        if (!key) return;
        nextSelected[key] = Boolean(checked);
      });
      return { newsletterRecipientsSelected: nextSelected };
    });
  };

  deleteNewsletterDraft = async (draftId) => {
    if (!draftId) return;
    const confirmed = window.confirm('Delete this draft? This cannot be undone.');
    if (!confirmed) return;

    this.setState({ newsletterDeletingDraftId: draftId, newsletterError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.deleteDraft(draftId), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      this.setState((prev) => {
        const nextDrafts = (prev.newsletterDrafts || []).filter((d) => d.id !== draftId);
        const deletedWasSelected = prev.newsletterSelectedDraftId === draftId;
        const nextSelected = deletedWasSelected ? (nextDrafts[0]?.id || null) : prev.newsletterSelectedDraftId;
        return {
          newsletterDrafts: nextDrafts,
          newsletterDeletingDraftId: null,
          newsletterBlogUrl: deletedWasSelected ? (nextDrafts[0]?.source_post_url || '') : prev.newsletterBlogUrl,
          newsletterSelectedDraftId: nextSelected,
          newsletterSubject: deletedWasSelected ? (nextDrafts[0]?.subject || '') : prev.newsletterSubject,
          newsletterPreviewText: deletedWasSelected ? (nextDrafts[0]?.preview_text || '') : prev.newsletterPreviewText,
          newsletterBodyText: deletedWasSelected ? (nextDrafts[0]?.body_text || '') : prev.newsletterBodyText,
          newsletterImageUrl: deletedWasSelected ? (nextDrafts[0]?.source_image_url || extractImageUrlFromHtml(nextDrafts[0]?.body_html) || '') : prev.newsletterImageUrl,
          newsletterImagePosition: deletedWasSelected ? extractImagePositionFromHtml(nextDrafts[0]?.body_html) : prev.newsletterImagePosition,
          newsletterCtaLabel: deletedWasSelected ? (nextDrafts[0]?.cta_primary_label || '') : prev.newsletterCtaLabel,
          newsletterCtaUrl: deletedWasSelected ? (nextDrafts[0]?.cta_primary_url || nextDrafts[0]?.source_post_url || '') : prev.newsletterCtaUrl,
          newsletterPreviewHtml: deletedWasSelected ? (nextDrafts[0]?.body_html || '') : prev.newsletterPreviewHtml,
          newsletterDirty: deletedWasSelected ? false : prev.newsletterDirty,
        };
      });
    } catch (err) {
      this.setState({
        newsletterDeletingDraftId: null,
        newsletterError: err.message || 'Failed to delete draft.',
      });
    }
  };

  sendNewsletterNow = async () => {
    const draftId = this.state.newsletterSelectedDraftId;
    if (!draftId) {
      this.setState({ newsletterError: 'Select a draft first.' });
      return;
    }

    const selectedRecipients = (this.state.newsletterRecipients || [])
      .map((r) => String(r?.email || '').trim().toLowerCase())
      .filter((email) => email && this.state.newsletterRecipientsSelected[email] !== false);

    if (selectedRecipients.length === 0) {
      this.setState({ newsletterError: 'Select at least one active recipient in Recipients.' });
      return;
    }

    this.setState({ newsletterSending: true, newsletterError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.sendNow(draftId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ selectedRecipients }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await this.fetchNewsletterDrafts();
      this.setState({ newsletterSending: false });
    } catch (err) {
      this.setState({
        newsletterError: err.message || 'Failed to send campaign.',
        newsletterSending: false,
      });
    }
  };

  scheduleNewsletterDraft = async () => {
    const draftId = this.state.newsletterSelectedDraftId;
    if (!draftId) {
      this.setState({ newsletterError: 'Select a draft first.' });
      return;
    }

    const scheduledAt = String(this.state.newsletterScheduleAt || '').trim();
    if (!scheduledAt) {
      this.setState({ newsletterError: 'Provide schedule date/time first.' });
      return;
    }

    this.setState({ newsletterScheduling: true, newsletterError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.schedule(draftId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          scheduledAt,
          timezone: this.state.newsletterScheduleTz || 'UTC',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await this.fetchNewsletterDrafts();
      this.setState({ newsletterScheduling: false, newsletterScheduleModalOpen: false });
    } catch (err) {
      this.setState({
        newsletterError: err.message || 'Failed to schedule campaign.',
        newsletterScheduling: false,
      });
    }
  };

  unscheduleNewsletterDraft = async () => {
    const draftId = this.state.newsletterSelectedDraftId;
    if (!draftId) {
      this.setState({ newsletterError: 'Select a draft first.' });
      return;
    }

    this.setState({ newsletterUnscheduling: true, newsletterError: null });
    try {
      const urls = this.getNewsletterUrlMap();
      const res = await fetch(urls.unschedule(draftId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await this.fetchNewsletterDrafts();
      this.setState({ newsletterUnscheduling: false });
    } catch (err) {
      this.setState({
        newsletterError: err.message || 'Failed to unschedule campaign.',
        newsletterUnscheduling: false,
      });
    }
  };

  openNewsletterScheduleModal = () => {
    if (!this.state.newsletterSelectedDraftId) {
      this.setState({ newsletterError: 'Select a draft first.' });
      return;
    }
    this.setState({ newsletterScheduleModalOpen: true });
  };

  closeNewsletterScheduleModal = () => {
    if (this.state.newsletterScheduling) return;
    this.setState({ newsletterScheduleModalOpen: false });
  };

  openNewsletterPreviewModal = () => {
    if (!this.state.newsletterPreviewHtml && String(this.state.newsletterBodyText || '').trim()) {
      this.refreshNewsletterPreview();
    }
    this.setState({ newsletterPreviewModalOpen: true });
  };

  closeNewsletterPreviewModal = () => {
    this.setState({ newsletterPreviewModalOpen: false });
  };

  setNewsletterPreviewDevice = (device) => {
    this.setState({ newsletterPreviewDevice: device === 'mobile' ? 'mobile' : 'desktop' });
  };

  fetchJourneyOverview = async (daysParam) => {
    const days = Number.isFinite(Number(daysParam)) ? Number(daysParam) : this.state.journeyDays;

    this.setState({ journeyLoading: true, journeyError: null, journeyDays: days });
    try {
      const res = await fetch(urlMap.adminJourneyOverview(days), {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      this.setState({
        journeyLoading: false,
        journeyTimeline: Array.isArray(data.timeline) ? data.timeline : [],
        journeyLines: Array.isArray(data.journeys) ? data.journeys : [],
        journeyEventAxis: Array.isArray(data.eventAxis) ? data.eventAxis : [],
        journeyEventTypeCounts: Array.isArray(data.eventTypeCounts) ? data.eventTypeCounts : [],
        journeyCompletionTrend: Array.isArray(data.completionTrend) ? data.completionTrend : [],
        journeyWarningSignals: data.warningSignals || {
          verifyWithoutLogin: { count: 0, samples: [] },
          scaffoldWithoutWriterSession: { count: 0, samples: [] },
          completedWriterWithoutReflection: { count: 0, samples: [] },
        },
        journeyWritingSessions: data.writingSessions || {
          completedCount: 0,
          abandonedCount: 0,
          mobileSeconds: 0,
          webSeconds: 0,
        },
        journeyWritingByUser: Array.isArray(data.writingByUser) ? data.writingByUser : [],
      });
    } catch (err) {
      this.setState({
        journeyLoading: false,
        journeyError: err.message || 'Failed to load journey overview.',
      });
    }
  };

  fetchScaffoldReviewQueue = async () => {
    this.setState({ scaffoldReviewLoading: true, scaffoldReviewError: null });
    try {
      const res = await fetch(urlMap.adminReviewScaffolds, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this.setState({
        scaffoldReviewItems: Array.isArray(data.items) ? data.items : [],
        scaffoldReviewLoading: false,
      });
    } catch (err) {
      this.setState({
        scaffoldReviewError: err.message || 'Failed to load scaffold review queue.',
        scaffoldReviewLoading: false,
      });
    }
  };

  openReviewModal = async (scaffoldId) => {
    if (!scaffoldId) return;
    this.setState({
      reviewModalOpen: true,
      reviewModalLoading: true,
      reviewModalError: null,
      reviewModalScaffold: null,
      reviewComment: '',
    });

    try {
      const res = await fetch(urlMap.adminReviewScaffoldDetail(scaffoldId), {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this.setState({ reviewModalScaffold: data, reviewModalLoading: false });
    } catch (err) {
      this.setState({
        reviewModalError: err.message || 'Failed to load scaffold details.',
        reviewModalLoading: false,
      });
    }
  };

  closeReviewModal = () => {
    this.setState({
      reviewModalOpen: false,
      reviewModalLoading: false,
      reviewModalError: null,
      reviewModalScaffold: null,
      reviewComment: '',
      reviewActionLoading: false,
    });
  };

  approveScaffold = async () => {
    const { reviewModalScaffold, reviewComment } = this.state;
    if (!reviewModalScaffold?.id) return;

    this.setState({ reviewActionLoading: true, reviewModalError: null });
    try {
      const res = await fetch(urlMap.adminApproveScaffold(reviewModalScaffold.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ comment: reviewComment || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      await this.fetchScaffoldReviewQueue();
      this.closeReviewModal();
    } catch (err) {
      this.setState({
        reviewModalError: err.message || 'Failed to approve scaffold.',
        reviewActionLoading: false,
      });
    }
  };

  sendBackScaffold = async () => {
    const { reviewModalScaffold, reviewComment } = this.state;
    if (!reviewModalScaffold?.id) return;
    if (!String(reviewComment || '').trim()) {
      this.setState({ reviewModalError: 'Add a comment before sending back.' });
      return;
    }

    this.setState({ reviewActionLoading: true, reviewModalError: null });
    try {
      const res = await fetch(urlMap.adminSendBackScaffold(reviewModalScaffold.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ comment: reviewComment }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      await this.fetchScaffoldReviewQueue();
      this.closeReviewModal();
    } catch (err) {
      this.setState({
        reviewModalError: err.message || 'Failed to send scaffold back.',
        reviewActionLoading: false,
      });
    }
  };

  fetchPublisherRoleRequests = async () => {
    this.setState({ requestListLoading: true, requestListError: null });
    try {
      const res = await fetch(urlMap.adminPublisherRoleRequests, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this.setState({ publisherRequests: Array.isArray(data.requests) ? data.requests : [], requestListLoading: false });
    } catch (err) {
      this.setState({ requestListError: err.message || 'Failed to load requests', requestListLoading: false });
    }
  };

  approvePublisherRequest = async (requestId) => {
    if (!requestId) return;
    this.setState((prev) => ({ approvingRequestIds: { ...prev.approvingRequestIds, [requestId]: true } }));
    try {
      const res = await fetch(urlMap.approvePublisherRoleRequest(requestId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await this.fetchPublisherRoleRequests();
    } catch (err) {
      this.setState({ requestListError: err.message || 'Could not approve request.' });
    } finally {
      this.setState((prev) => {
        const next = { ...prev.approvingRequestIds };
        delete next[requestId];
        return { approvingRequestIds: next };
      });
    }
  };

  fetchList = async () => {
    this.setState({ listLoading: true, listError: null });
    try {
      const res = await fetch(urlMap.adminBackupList(1, 10), {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this.setState({ listItems: data.items ?? [], bucket: data.bucket ?? '', listLoading: false });
    } catch (err) {
      this.setState({ listError: err.message || 'Failed to load', listLoading: false });
    }
  };

  runBackup = async () => {
    this.setState({ loading: true, result: null, runError: null });
    try {
      const res = await fetch(urlMap.adminBackup, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      this.setState({ result: data, loading: false });
      this.fetchList();
    } catch (err) {
      this.setState({ runError: err.message || 'Request failed', loading: false });
    }
  };

  exportJourneyPdf = () => {
    const {
      journeyDays,
      journeyLines,
      journeyEventAxis,
      journeyEventTypeCounts,
      journeyCompletionTrend,
      journeyWarningSignals,
      journeyWritingSessions,
      journeyWritingByUser,
    } = this.state;

    const lines = Array.isArray(journeyLines) ? journeyLines : [];
    const maxMinute = Math.max(
      1,
      ...lines.flatMap((line) => (Array.isArray(line?.points) ? line.points.map((p) => Number(p?.minute || 0)) : [0])),
    );

    const chartWidth = 920;
    const chartHeight = 360;
    const pad = { top: 22, right: 18, bottom: 42, left: 220 };

    const warningRows = [
      ['Verified but never logged in', Number(journeyWarningSignals?.verifyWithoutLogin?.count || 0)],
      ['Scaffold created but session never started', Number(journeyWarningSignals?.scaffoldWithoutWriterSession?.count || 0)],
      ['Writing completed but reflection dropped', Number(journeyWarningSignals?.completedWriterWithoutReflection?.count || 0)],
    ];

    const topEvents = (Array.isArray(journeyEventTypeCounts) ? journeyEventTypeCounts : []).slice(0, 12);
    const byUserRows = (Array.isArray(journeyWritingByUser) ? journeyWritingByUser : []).slice(0, 50);
    const completionTrendRows = (Array.isArray(journeyCompletionTrend) ? journeyCompletionTrend : []).slice(-90);
    const totalSessionOutcomes = Number(journeyWritingSessions?.completedCount || 0) + Number(journeyWritingSessions?.abandonedCount || 0);
    const completionRate = totalSessionOutcomes > 0
      ? `${Math.round((Number(journeyWritingSessions?.completedCount || 0) / totalSessionOutcomes) * 100)}%`
      : '0%';

    const svgRows = [0, 360, 720, 1080, 1439].map((timeMinute) => {
      const y = pointY({ timeOfDayMinute: timeMinute }, chartHeight, pad);
      return `
        <g>
          <line x1="${pad.left}" y1="${y}" x2="${chartWidth - pad.right}" y2="${y}" stroke="#e2e8f0" stroke-width="1" />
          <text x="${pad.left - 10}" y="${y + 4}" text-anchor="end" fill="#64748b" font-size="9" font-family="monospace">${escapeHtml(formatTimeOfDay(timeMinute))}</text>
        </g>
      `;
    }).join('');

    const svgTicks = [0, 0.25, 0.5, 0.75, 1].map((tick) => {
      const minute = Math.round(tick * maxMinute);
      const x = pad.left + tick * (chartWidth - pad.left - pad.right);
      return `
        <g>
          <line x1="${x}" y1="${pad.top}" x2="${x}" y2="${chartHeight - pad.bottom}" stroke="#e2e8f0" stroke-width="1" stroke-dasharray="3 3" />
          <text x="${x}" y="${chartHeight - pad.bottom + 14}" text-anchor="middle" fill="#64748b" font-size="10" font-family="monospace">${minute}m</text>
        </g>
      `;
    }).join('');

    const svgLines = lines.map((line, idx) => {
      const d = buildJourneyPath(line?.points, maxMinute, chartWidth, chartHeight, pad);
      if (!d) return '';
      return `<path d="${d}" fill="none" stroke="${journeyColor(idx)}" stroke-width="2" stroke-linecap="round" opacity="0.9" />`;
    }).join('');

    const svgPoints = lines.map((line, idx) => {
      const color = journeyColor(idx);
      const points = Array.isArray(line?.points) ? line.points : [];
      return points.map((point) => {
        const minute = Math.max(0, Number(point?.minute || 0));
        const x = pad.left + ((minute / Math.max(1, maxMinute)) * (chartWidth - pad.left - pad.right));
        const y = pointY(point, chartHeight, pad);
        return `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="3.2" fill="${color}" opacity="0.95" />`;
      }).join('');
    }).join('');

    const svgLegend = lines.slice(0, 6).map((line, idx) => {
      const y = pad.top + (idx * 16);
      const label = `U${line?.userId ?? 'n/a'} · ${String(line?.sessionId || '').slice(0, 8)}`;
      return `
        <g>
          <line x1="${chartWidth - pad.right - 170}" y1="${y}" x2="${chartWidth - pad.right - 150}" y2="${y}" stroke="${journeyColor(idx)}" stroke-width="2" />
          <text x="${chartWidth - pad.right - 146}" y="${y + 4}" fill="#64748b" font-size="10" font-family="monospace">${escapeHtml(label)}</text>
        </g>
      `;
    }).join('');

    const trendWidth = 920;
    const trendHeight = 280;
    const trendPad = { top: 20, right: 52, bottom: 44, left: 52 };
    const trendPlotWidth = trendWidth - trendPad.left - trendPad.right;
    const trendPlotHeight = trendHeight - trendPad.top - trendPad.bottom;
    const maxStartedCount = Math.max(10, ...completionTrendRows.map((row) => Number(row?.startedCount || 0)));
    const trendX = (idx) => {
      if (completionTrendRows.length <= 1) return trendPad.left;
      return trendPad.left + ((idx / (completionTrendRows.length - 1)) * trendPlotWidth);
    };
    const trendYRate = (ratePct) => {
      const safeRate = Math.max(0, Math.min(100, Number(ratePct) || 0));
      return trendPad.top + ((100 - safeRate) / 100) * trendPlotHeight;
    };
    const trendYStarted = (count) => {
      const safeCount = Math.max(0, Number(count) || 0);
      return trendPad.top + (1 - (safeCount / Math.max(1, maxStartedCount))) * trendPlotHeight;
    };

    const trendBars = completionTrendRows.map((row, idx) => {
      const x = trendX(idx);
      const barWidth = Math.max(3, trendPlotWidth / Math.max(12, completionTrendRows.length * 1.7));
      const y = trendYStarted(row?.startedCount || 0);
      const h = Math.max(1, (trendPad.top + trendPlotHeight) - y);
      const fill = row?.lowConfidence ? '#fca5a5' : '#cbd5e1';
      return `<rect x="${(x - (barWidth / 2)).toFixed(2)}" y="${y.toFixed(2)}" width="${barWidth.toFixed(2)}" height="${h.toFixed(2)}" fill="${fill}" opacity="0.65" />`;
    }).join('');

    const trendRatePath = completionTrendRows.map((row, idx) => {
      const x = trendX(idx);
      const y = trendYRate(row?.completionRatePct || 0);
      return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ');

    const trendAvgPath = completionTrendRows.map((row, idx) => {
      const x = trendX(idx);
      const y = trendYRate(row?.movingAvg7dPct || 0);
      return `${idx === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ');

    const trendDots = completionTrendRows.map((row, idx) => {
      const x = trendX(idx);
      const y = trendYRate(row?.completionRatePct || 0);
      const marker = row?.lowConfidence ? `<circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="4.2" fill="none" stroke="#dc2626" stroke-width="1.3" />` : '';
      return `
        <circle cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="2.8" fill="#0ea5e9" />
        ${marker}
      `;
    }).join('');

    const trendRateTicks = [0, 25, 50, 75, 100].map((tick) => {
      const y = trendYRate(tick);
      return `
        <g>
          <line x1="${trendPad.left}" y1="${y.toFixed(2)}" x2="${(trendWidth - trendPad.right).toFixed(2)}" y2="${y.toFixed(2)}" stroke="#e5e7eb" stroke-width="1" />
          <text x="${(trendPad.left - 8).toFixed(2)}" y="${(y + 4).toFixed(2)}" text-anchor="end" fill="#64748b" font-size="10" font-family="monospace">${tick}%</text>
        </g>
      `;
    }).join('');

    const trendStartedTicks = [0, 0.5, 1].map((ratio) => {
      const count = Math.round(ratio * maxStartedCount);
      const y = trendYStarted(count);
      return `<text x="${(trendWidth - trendPad.right + 8).toFixed(2)}" y="${(y + 4).toFixed(2)}" fill="#64748b" font-size="10" font-family="monospace">${count}</text>`;
    }).join('');

    const trendXTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
      const idx = Math.min(completionTrendRows.length - 1, Math.max(0, Math.round(ratio * Math.max(0, completionTrendRows.length - 1))));
      const row = completionTrendRows[idx];
      const x = trendX(idx);
      const label = row?.day ? String(row.day).slice(5) : '';
      return `
        <g>
          <line x1="${x.toFixed(2)}" y1="${trendPad.top}" x2="${x.toFixed(2)}" y2="${(trendPad.top + trendPlotHeight).toFixed(2)}" stroke="#f1f5f9" stroke-width="1" />
          <text x="${x.toFixed(2)}" y="${(trendHeight - trendPad.bottom + 16).toFixed(2)}" text-anchor="middle" fill="#64748b" font-size="10" font-family="monospace">${svgEscape(label)}</text>
        </g>
      `;
    }).join('');

    const exportWindow = window.open('', '_blank', 'width=1280,height=920');
    if (!exportWindow) return;

    exportWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>User Journey Analytics Export</title>
          <style>
            @page { margin: 12mm; }
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            body { margin: 0; padding: 20px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111827; }
            body::before {
              content: 'Storyclub.ai';
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-28deg);
              font-size: 72px;
              font-weight: 700;
              color: rgba(17, 24, 39, 0.08);
              letter-spacing: 2px;
              z-index: 0;
              pointer-events: none;
              white-space: nowrap;
            }
            .report { position: relative; z-index: 1; }
            .head { margin-bottom: 14px; }
            h1 { margin: 0; font-size: 22px; }
            .meta { margin: 4px 0 0; color: #6b7280; font-size: 12px; }
            .grid { display: grid; grid-template-columns: repeat(5, minmax(120px, 1fr)); gap: 10px; margin-top: 12px; }
            .kpi { border: 1px solid #d1d5db; border-radius: 8px; padding: 10px; }
            .kpi label { display: block; font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .04em; }
            .kpi strong { font-size: 18px; line-height: 1; }
            .card { border: 1px solid #d1d5db; border-radius: 10px; margin-top: 14px; overflow: hidden; }
            .card h2 { margin: 0; padding: 12px 16px; font-size: 14px; border-bottom: 1px solid #d1d5db; }
            .body { padding: 12px 16px; }
            .warns { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 8px; margin-bottom: 10px; }
            .warn { border: 1px solid #d1d5db; border-radius: 8px; padding: 6px 8px; display: grid; grid-template-columns: auto 1fr auto; gap: 8px; align-items: center; }
            .warn i { width: 16px; height: 16px; border-radius: 999px; border: 1px solid #93c5fd; color: #1d4ed8; display: grid; place-items: center; font-style: normal; font-size: 10px; font-weight: 700; }
            .warn b { color: #b45309; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th, td { border-top: 1px solid #e5e7eb; padding: 7px 8px; text-align: left; }
            thead th { border-top: none; background: #f8fafc; color: #475569; }
            .mono { font-family: ui-monospace, Menlo, Monaco, monospace; }
          </style>
        </head>
        <body>
          <div class="report">
            <div class="head">
              <h1>User Journey Analytics</h1>
              <p class="meta">Window: last ${Number(journeyDays || 30)} days · Generated: ${escapeHtml(new Date().toLocaleString())}</p>
            </div>

            <div class="grid">
              <div class="kpi"><label>Completed sessions</label><strong>${Number(journeyWritingSessions?.completedCount || 0)}</strong></div>
              <div class="kpi"><label>Broken sessions</label><strong>${Number(journeyWritingSessions?.abandonedCount || 0)}</strong></div>
              <div class="kpi"><label>Mobile writing per session</label><strong>${escapeHtml(formatMinutesPerSession(journeyWritingSessions?.mobileSeconds || 0, totalSessionOutcomes))}</strong></div>
              <div class="kpi"><label>Web writing per session</label><strong>${escapeHtml(formatMinutesPerSession(journeyWritingSessions?.webSeconds || 0, totalSessionOutcomes))}</strong></div>
              <div class="kpi"><label>Outcome completion rate</label><strong>${completionRate}</strong></div>
            </div>

            <div class="card">
              <h2>Session completion engagement</h2>
              <div class="body">
                ${completionTrendRows.length === 0 ? '<p>No writing session trend data in this window.</p>' : `
                <svg viewBox="0 0 ${trendWidth} ${trendHeight}" role="img" aria-label="Daily completion rate with 7-day average and started sessions" style="width:100%;min-width:900px;display:block;">
                  <rect x="0" y="0" width="${trendWidth}" height="${trendHeight}" fill="transparent" />
                  ${trendRateTicks}
                  ${trendXTicks}
                  ${trendBars}
                  <path d="${trendRatePath}" fill="none" stroke="#0ea5e9" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="${trendAvgPath}" fill="none" stroke="#0f766e" stroke-width="2" stroke-dasharray="5 4" stroke-linecap="round" stroke-linejoin="round" />
                  ${trendDots}
                  <line x1="${trendPad.left}" y1="${(trendPad.top + trendPlotHeight).toFixed(2)}" x2="${(trendWidth - trendPad.right).toFixed(2)}" y2="${(trendPad.top + trendPlotHeight).toFixed(2)}" stroke="#94a3b8" stroke-width="1.2" />
                  <line x1="${trendPad.left}" y1="${trendPad.top}" x2="${trendPad.left}" y2="${(trendPad.top + trendPlotHeight).toFixed(2)}" stroke="#94a3b8" stroke-width="1.2" />
                  <line x1="${(trendWidth - trendPad.right).toFixed(2)}" y1="${trendPad.top}" x2="${(trendWidth - trendPad.right).toFixed(2)}" y2="${(trendPad.top + trendPlotHeight).toFixed(2)}" stroke="#94a3b8" stroke-width="1.2" />
                  ${trendStartedTicks}
                  <text x="${(trendPad.left).toFixed(2)}" y="${(trendPad.top - 6).toFixed(2)}" fill="#64748b" font-size="10" font-family="monospace">Completion rate</text>
                  <text x="${(trendWidth - trendPad.right + 8).toFixed(2)}" y="${(trendPad.top - 6).toFixed(2)}" fill="#64748b" font-size="10" font-family="monospace">Started sessions</text>
                </svg>
                <p class="meta">Blue line: daily completion rate. Teal dashed: 7-day moving average. Gray bars: started sessions. Red outlined points indicate days with fewer than 10 started sessions.</p>`}
              </div>
            </div>

            <div class="card">
              <h2>Journey timeline</h2>
              <div class="body">
                <div class="warns">
                  ${warningRows.map(([name, count]) => `<div class="warn"><i>i</i><span>${escapeHtml(name)}</span><b>${count}</b></div>`).join('')}
                </div>
                ${lines.length === 0 ? '<p>No events in this window.</p>' : `
                <svg viewBox="0 0 ${chartWidth} ${chartHeight}" role="img" aria-label="User journey timeline chart" style="width:100%;min-width:900px;display:block;">
                  <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" fill="transparent" />
                  ${svgRows}
                  <line x1="${pad.left}" y1="${chartHeight - pad.bottom}" x2="${chartWidth - pad.right}" y2="${chartHeight - pad.bottom}" stroke="#94a3b8" stroke-width="1.2" />
                  <line x1="${pad.left}" y1="${pad.top}" x2="${pad.left}" y2="${chartHeight - pad.bottom}" stroke="#94a3b8" stroke-width="1.2" />
                  ${svgTicks}
                  ${svgLines}
                  ${svgPoints}
                  ${svgLegend}
                </svg>`}
              </div>
            </div>

            <div class="card">
              <h2>Top journey events</h2>
              <div class="body">
                <table>
                  <thead><tr><th>Event</th><th>Count</th></tr></thead>
                  <tbody>
                    ${topEvents.length > 0
                      ? topEvents.map((row) => `<tr><td>${escapeHtml(row?.eventType || '')}</td><td class="mono">${Number(row?.count || 0)}</td></tr>`).join('')
                      : '<tr><td colspan="2">No event data in this window.</td></tr>'}
                  </tbody>
                </table>
              </div>
            </div>

            <div class="card">
              <h2>Time spent per user by platform</h2>
              <div class="body">
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Completed</th>
                      <th>Broken</th>
                      <th>Mobile / session</th>
                      <th>Web / session</th>
                      <th>Avg / session</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${byUserRows.length > 0
                      ? byUserRows.map((row) => {
                        const rowSessionCount = Number(row?.completedCount || 0) + Number(row?.abandonedCount || 0);
                        return `
                        <tr>
                          <td class="mono">#${Number(row?.userId || 0)}</td>
                          <td>${Number(row?.completedCount || 0)}</td>
                          <td>${Number(row?.abandonedCount || 0)}</td>
                          <td>${escapeHtml(formatMinutesPerSession(row?.mobileSeconds || 0, rowSessionCount))}</td>
                          <td>${escapeHtml(formatMinutesPerSession(row?.webSeconds || 0, rowSessionCount))}</td>
                          <td class="mono">${escapeHtml(formatMinutesPerSession(row?.totalSeconds || 0, rowSessionCount))}</td>
                        </tr>
                      `;
                      }).join('')
                      : '<tr><td colspan="6">No writing-session stats available in this window.</td></tr>'}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </body>
      </html>
    `);

    exportWindow.document.close();
    exportWindow.focus();
    exportWindow.print();
  };

  render() {
    const role = getEffectiveRole();
    if (role !== 'publisher') return <Navigate to={appNavigation.home} replace />;
    const isEmbedded = this.props?.embedded
      || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1');

    const {
      activeTab,
      loading,
      result,
      runError,
      listLoading,
      listItems,
      listError,
      bucket,
      requestListLoading,
      requestListError,
      publisherRequests,
      approvingRequestIds,
      scaffoldReviewLoading,
      scaffoldReviewError,
      scaffoldReviewItems,
      reviewModalOpen,
      reviewModalLoading,
      reviewModalError,
      reviewModalScaffold,
      reviewComment,
      reviewActionLoading,
      journeyLoading,
      journeyError,
      journeyDays,
      journeyTimeline,
      journeyLines,
      journeyEventAxis,
      journeyEventTypeCounts,
      journeyCompletionTrend,
      journeyWarningSignals,
      journeyWritingSessions,
      journeyWritingByUser,
      newsletterBlogUrl,
      newsletterSeedUrlSavedGlow,
      newsletterGenerating,
      newsletterError,
      newsletterSubscribersLoading,
      newsletterSubscribersError,
      newsletterSubscribersExpanded,
      newsletterSubscribers,
      newsletterRecipients,
      newsletterRecipientsSelected,
      newsletterRecipientsModalOpen,
      newsletterDraftsLoading,
      newsletterDraftsError,
      newsletterDrafts,
      newsletterCreatingDraft,
      newsletterDeletingDraftId,
      newsletterOpeningDraftId,
      newsletterSelectedDraftId,
      newsletterSubject,
      newsletterPreviewText,
      newsletterBodyText,
      newsletterImageUrl,
      newsletterImagePosition,
      newsletterCtaLabel,
      newsletterCtaUrl,
      newsletterPreviewHtml,
      newsletterPreviewModalOpen,
      newsletterPreviewLoading,
      newsletterPreviewDevice,
      newsletterSaving,
      newsletterAutoSaving,
      newsletterDirty,
      newsletterSending,
      newsletterScheduling,
      newsletterUnscheduling,
      newsletterScheduleModalOpen,
      newsletterScheduleAt,
      newsletterScheduleTz,
    } = this.state;

    const activeRecipientsCount = (newsletterRecipients || []).filter((r) => {
      const email = String(r?.email || '').trim().toLowerCase();
      return email && newsletterRecipientsSelected[email] !== false;
    }).length;

    const recipientKeys = (newsletterRecipients || [])
      .map((item) => String(item?.email || '').trim().toLowerCase())
      .filter(Boolean);
    const recipientsAllSelected = recipientKeys.length > 0
      && recipientKeys.every((email) => newsletterRecipientsSelected[email] !== false);

    const subscriberKeys = (newsletterSubscribers || [])
      .map((item) => String(item?.email || '').trim().toLowerCase())
      .filter(Boolean);
    const subscribersAllSelected = subscriberKeys.length > 0
      && subscriberKeys.every((email) => newsletterRecipientsSelected[email] !== false);

    const selectedNewsletterDraft = (newsletterDrafts || []).find((d) => d.id === newsletterSelectedDraftId) || null;
    const formatScheduledLabel = (draft) => {
      if (!draft?.scheduled_at) return null;
      const tz = String(draft?.timezone || 'UTC').trim() || 'UTC';
      const when = new Date(draft.scheduled_at);
      if (Number.isNaN(when.getTime())) return null;
      try {
        const formatted = new Intl.DateTimeFormat(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
          timeZone: tz,
        }).format(when);
        return `${formatted} (${tz})`;
      } catch {
        return `${when.toLocaleString()} (${tz})`;
      }
    };
    const selectedDraftScheduledLabel = formatScheduledLabel(selectedNewsletterDraft);
    const campaignTitle = String(newsletterSubject || '').trim() || '(no subject yet)';
    const campaignStatusRaw = String(selectedNewsletterDraft?.status || 'draft').trim().toLowerCase();
    const campaignStatusLabel = campaignStatusRaw === 'sent'
      ? 'Finished'
      : (campaignStatusRaw ? `${campaignStatusRaw.charAt(0).toUpperCase()}${campaignStatusRaw.slice(1)}` : 'Draft');

    return (
      <div className="profile-page">
        {!isEmbedded ? (
          <TopBar
            pageTitle="Publisher Panel"
            pageSubtitle="Newsletter tools for publishers"
            backTo={appNavigation.home}
            backLabel="Back to work"
          />
        ) : null}

        <main className="profile-main publisher-main">
          <div className="admin-page publisher-page">
            <div className={`admin-wrap publisher-wrap ${activeTab === 'journey' ? 'admin-wrap--journey' : ''}`}>
          {!isEmbedded ? (
            <TabsMenu
              className="admin-tabs"
              ariaLabel="Publisher sections"
              activeKey={activeTab}
              onChange={this.handleTabChange}
              tabs={[{ key: 'newsletter', label: 'Newsletter Agent' }]}
            />
          ) : null}

          {activeTab === 'newsletter' && (
            <>
              <div className="app-body publisher-news-shell admin-card--spaced">
                <aside className="menu menu--project-page publisher-news-sidebar">
                  <div className="admin-table-card admin-news-draft-rail">
                  <div className="admin-table-head">
                    <h2 className="admin-table-title">Campaigns</h2>
                  </div>
                  {newsletterDraftsError && <p className="admin-error admin-error-pad">{newsletterDraftsError}</p>}
                  {!newsletterDraftsLoading && !newsletterDraftsError && newsletterDrafts.length === 0 && (
                    <p className="admin-empty">No previous campaigns yet. Create one with New campaign and Start agent.</p>
                  )}
                  {newsletterDrafts.length > 0 && (
                    <div className="admin-news-draft-list">
                      {newsletterDrafts.map((draft) => {
                        const isSelected = draft.id === newsletterSelectedDraftId;
                        const draftScheduledLabel = formatScheduledLabel(draft);
                        const isOpening = newsletterOpeningDraftId === draft.id;
                        return (
                          <div key={draft.id} className={`admin-news-draft-item ${isSelected ? 'is-active' : ''}`}>
                            <button type="button" className="admin-news-draft-open" onClick={() => this.loadNewsletterDraftById(draft.id)} disabled={isOpening}>
                              <span className="admin-news-draft-subject">{draft.subject || '(no subject)'}</span>
                              <span className="admin-news-draft-status">{isOpening ? 'loading' : (draft.status || 'draft')}</span>
                              {draftScheduledLabel ? <span className="admin-news-draft-schedule">Scheduled: {draftScheduledLabel}</span> : null}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  </div>
                </aside>

                <section className="content publisher-news-content">
                  <div className="content-main editor-left publisher-news-main">
                  <div className="publisher-news-top-row">
                    <div className="admin-table-card publisher-news-title-card">
                      <div className="publisher-news-title-meta">                       
                        <div className="publisher-news-title-line">
                          <div className="publisher-news-title-value">{campaignTitle}</div>
                          <span className={`publisher-news-status-chip is-${campaignStatusRaw || 'draft'}`}>
                            Status: {campaignStatusLabel}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="admin-table-card publisher-news-cta-card">
                      <div className="admin-table-head publisher-news-cta-head">
                        <div className="admin-news-head-actions publisher-news-cta-actions">
                          <button
                            type="button"
                            className="btn primary admin-refresh-btn"
                            onClick={this.createNewsletterDraft}
                            disabled={newsletterCreatingDraft || newsletterDraftsLoading}
                          >
                            {newsletterCreatingDraft ? 'Creating…' : 'New campaign'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="admin-table-card">                    
                    <div className="publisher-news-seed-body">
                      <div className="admin-news-seed-head">
                        <label className="admin-news-seed-field">
                          <span>Seed URL</span>
                          <input
                            type="url"
                            className={newsletterSeedUrlSavedGlow ? 'is-saved' : ''}
                            value={newsletterBlogUrl}
                            onChange={(e) => this.handleNewsletterSeedUrlChange(e.target.value)}
                            placeholder="https://storyclub.ai/blog/your-post"
                          />
                        </label>
                        <button
                          type="button"
                          className="btn primary"
                          onClick={this.generateNewsletterDraft}
                          disabled={newsletterGenerating}
                        >
                          {newsletterGenerating ? 'Generating…' : 'Start agent'}
                        </button>
                      </div>
                    </div>
                    {newsletterError && <p className="admin-error admin-error-pad">{newsletterError}</p>}
                  </div>

                  <div className="admin-table-card">
                    <div className="admin-table-head">
                      <h2 className="admin-table-title">
                        <button
                          type="button"
                          className="admin-news-collapse-toggle"
                          onClick={() => this.setState((prev) => ({ newsletterSubscribersExpanded: !prev.newsletterSubscribersExpanded }))}
                          aria-expanded={newsletterSubscribersExpanded}
                          aria-label={newsletterSubscribersExpanded ? 'Collapse active subscribers' : 'Expand active subscribers'}
                        >
                          <span className={`admin-news-collapse-chevron${newsletterSubscribersExpanded ? ' is-open' : ''}`} aria-hidden="true">
                            ▸
                          </span>
                          <span>Active Subscribers</span>
                        </button>
                      </h2>
                      <div className="admin-news-head-actions">
                        <button type="button" className="btn subtle admin-refresh-btn" onClick={this.fetchNewsletterSubscribers} disabled={newsletterSubscribersLoading}>
                          {newsletterSubscribersLoading ? 'Loading…' : 'Refresh subscribers'}
                        </button>
                      </div>
                    </div>
                    {newsletterSubscribersError && <p className="admin-error admin-error-pad">{newsletterSubscribersError}</p>}
                    {!newsletterSubscribersExpanded ? null : !newsletterSubscribersLoading && !newsletterSubscribersError && newsletterSubscribers.length === 0 && (
                      <p className="admin-empty">No active subscribers found.</p>
                    )}
                    {newsletterSubscribersExpanded && newsletterSubscribers.length > 0 && (
                      <div className="admin-subscribers-scroll">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th className="admin-th admin-th-left">
                                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  <input
                                    type="checkbox"
                                    className="admin-news-recipient-check"
                                    checked={subscribersAllSelected}
                                    onChange={(e) => this.setAllNewsletterSubscribersSelected(e.target.checked)}
                                  />
                                  <span>Select all</span>
                                </label>
                              </th>
                              <th className="admin-th admin-th-left">Email</th>
                              <th className="admin-th">Source</th>
                              <th className="admin-th admin-th-right">Subscribed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {newsletterSubscribers.map((item, i) => {
                              const emailKey = String(item?.email || '').trim().toLowerCase();
                              const isRecipient = emailKey ? newsletterRecipientsSelected[emailKey] !== false : false;
                              return (
                                <tr key={item.id || item.email || i} className={i === 0 ? 'admin-row-first' : 'admin-row'}>
                                  <td className="admin-td admin-td-left admin-td-muted">
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                      <input
                                        type="checkbox"
                                        className="admin-news-recipient-check"
                                        checked={isRecipient}
                                        onChange={(e) => this.setNewsletterRecipientSelected(emailKey, e.target.checked)}
                                      />
                                      <span>{isRecipient ? 'Active' : 'Inactive'}</span>
                                    </label>
                                  </td>
                                  <td className="admin-td admin-td-left">{item.email}</td>
                                  <td className="admin-td admin-td-muted">{item.source || '-'}</td>
                                  <td className="admin-td admin-td-right admin-mono">{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="admin-table-card">
                    <div className="admin-table-head">
                      <h2 className="admin-table-title">Draft Editor</h2>
                      <div className="admin-news-head-actions admin-news-head-actions--editor">
                        <button type="button" className="btn subtle admin-refresh-btn" onClick={this.saveNewsletterDraft} disabled={newsletterSaving}>
                          {newsletterSaving ? 'Saving…' : 'Save draft'}
                        </button>
                        <button type="button" className="btn subtle admin-refresh-btn" onClick={this.openNewsletterScheduleModal} disabled={newsletterScheduling}>
                          Schedule send
                        </button>
                        <button type="button" className="btn primary admin-refresh-btn" onClick={this.openNewsletterPreviewModal}>
                          Open preview
                        </button>
                      </div>
                    </div>
                    <div className="admin-news-editor">
                      <label className="admin-news-field">
                        <span>Subject</span>
                        <input value={newsletterSubject} onChange={(e) => this.updateNewsletterField('newsletterSubject', e.target.value)} />
                      </label>
                      <label className="admin-news-field">
                        <span>Preview Text</span>
                        <input value={newsletterPreviewText} onChange={(e) => this.updateNewsletterField('newsletterPreviewText', e.target.value)} />
                      </label>
                      <label className="admin-news-field">
                        <span>Image URL (from blog excerpt)</span>
                        <input value={newsletterImageUrl} onChange={(e) => this.updateNewsletterField('newsletterImageUrl', e.target.value)} />
                      </label>
                      <label className="admin-news-field">
                        <span>Image Position</span>
                        <select value={newsletterImagePosition} onChange={(e) => this.updateNewsletterField('newsletterImagePosition', e.target.value)}>
                          <option value="top">Top</option>
                          <option value="middle">Middle</option>
                          <option value="bottom">Bottom</option>
                        </select>
                      </label>
                      <label className="admin-news-field">
                        <span>CTA Label</span>
                        <input value={newsletterCtaLabel} onChange={(e) => this.updateNewsletterField('newsletterCtaLabel', e.target.value)} />
                      </label>
                      <label className="admin-news-field">
                        <span>CTA URL</span>
                        <input value={newsletterCtaUrl} onChange={(e) => this.updateNewsletterField('newsletterCtaUrl', e.target.value)} />
                      </label>
                      <label className="admin-news-field">
                        <span>Proposed Email Content</span>
                        <textarea rows={12} value={newsletterBodyText} onChange={(e) => this.updateNewsletterField('newsletterBodyText', e.target.value)} />
                      </label>
                    </div>
                  </div>

                  <div className="admin-table-card publisher-news-danger-zone">
                    <div className="admin-table-head publisher-news-danger-zone__head">
                      <h2 className="admin-table-title">Danger Zone</h2>
                      <button
                        type="button"
                        className="btn danger admin-refresh-btn"
                        onClick={() => this.deleteNewsletterDraft(newsletterSelectedDraftId)}
                        disabled={!newsletterSelectedDraftId || newsletterDeletingDraftId === newsletterSelectedDraftId}
                      >
                        {newsletterDeletingDraftId === newsletterSelectedDraftId ? 'Deleting…' : 'Delete campaign'}
                      </button>
                    </div>
                  </div>
                    </div>
                  </section>
              </div>
            </>
          )}

          <Modal isOpen={newsletterScheduleModalOpen} onClose={this.closeNewsletterScheduleModal}>
            <div className="admin-review-modal">
              <h3 className="admin-review-modal__title">Schedule Newsletter Send</h3>
              <div className="admin-review-modal__content">
                {selectedDraftScheduledLabel ? (
                  <div className="admin-review-modal__actions" style={{ justifyContent: 'space-between' }}>
                    <p className="admin-news-editor-meta" style={{ margin: 0 }}>
                      Scheduled send: {selectedDraftScheduledLabel}
                    </p>
                    <button
                      type="button"
                      className="btn subtle"
                      onClick={this.unscheduleNewsletterDraft}
                      disabled={newsletterUnscheduling || newsletterScheduling}
                    >
                      {newsletterUnscheduling ? 'Unscheduling…' : 'Unschedule'}
                    </button>
                  </div>
                ) : null}

                <label className="admin-news-field">
                  <span>Schedule datetime</span>
                  <input
                    type="datetime-local"
                    value={newsletterScheduleAt}
                    onChange={(e) => this.setState({ newsletterScheduleAt: e.target.value })}
                  />
                </label>

                <label className="admin-news-field">
                  <span>Timezone</span>
                  <select
                    value={newsletterScheduleTz}
                    onChange={(e) => this.setState({ newsletterScheduleTz: e.target.value })}
                  >
                    {NEWSLETTER_SCHEDULE_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </label>

                <div className="admin-review-modal__actions">
                  <button
                    type="button"
                    className="btn subtle"
                    onClick={this.closeNewsletterScheduleModal}
                    disabled={newsletterScheduling}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={this.scheduleNewsletterDraft}
                    disabled={newsletterScheduling}
                  >
                    {newsletterScheduling ? 'Scheduling…' : 'Confirm schedule'}
                  </button>
                </div>
              </div>
            </div>
          </Modal>

          <Modal isOpen={newsletterPreviewModalOpen} onClose={this.closeNewsletterPreviewModal}>
            <div className="admin-review-modal admin-news-preview-modal">
              <h3 className="admin-review-modal__title">Newsletter Email Preview</h3>
              <div className="admin-news-preview-toolbar" role="group" aria-label="Preview user agent">
                <span className="admin-news-preview-toolbar__label">User agent</span>
                <button
                  type="button"
                  className={`btn subtle admin-news-preview-device ${newsletterPreviewDevice === 'desktop' ? 'is-active' : ''}`}
                  onClick={() => this.setNewsletterPreviewDevice('desktop')}
                >
                  Desktop
                </button>
                <button
                  type="button"
                  className={`btn subtle admin-news-preview-device ${newsletterPreviewDevice === 'mobile' ? 'is-active' : ''}`}
                  onClick={() => this.setNewsletterPreviewDevice('mobile')}
                >
                  Mobile
                </button>
              </div>
              <div className="admin-news-actions" style={{ marginBottom: 10 }}>
                <button type="button" className="btn subtle" onClick={this.openNewsletterRecipientsModal}>
                  Recipients ({activeRecipientsCount})
                </button>
                <button type="button" className="btn primary" onClick={this.sendNewsletterNow} disabled={newsletterSending}>
                  {newsletterSending ? 'Sending…' : 'Send now'}
                </button>
              </div>
              {newsletterPreviewLoading && <p className="admin-muted">Rendering template…</p>}
              <div className="admin-news-client-shell" aria-label="Mail client preview shell">
                <div className="admin-news-client-row">
                  <span className="admin-news-client-label">From</span>
                  <span className="admin-news-client-value">Storyclub &lt;support@storyclub.ai&gt;</span>
                </div>
                <div className="admin-news-client-row">
                  <span className="admin-news-client-label">Subject</span>
                  <span className="admin-news-client-value">{newsletterSubject || '(no subject)'}</span>
                </div>
              </div>
              {!!newsletterPreviewHtml ? (
                <div className={`admin-news-preview-frame-wrap ${newsletterPreviewDevice === 'mobile' ? 'is-mobile' : 'is-desktop'}`}>
                  <iframe
                    title="Newsletter email preview"
                    className={`admin-news-preview-frame ${newsletterPreviewDevice === 'mobile' ? 'is-mobile' : 'is-desktop'}`}
                    srcDoc={this.getThemeAwareNewsletterPreviewHtml(newsletterPreviewHtml, newsletterPreviewDevice)}
                  />
                </div>
              ) : (
                <p className="admin-muted">No preview yet. Add content to generate a preview.</p>
              )}
            </div>
          </Modal>

          <Modal isOpen={newsletterRecipientsModalOpen} onClose={this.closeNewsletterRecipientsModal}>
            <div className="admin-review-modal">
              <h3 className="admin-review-modal__title">Subscribers Send List</h3>
              <div className="admin-review-modal__content">
                <p className="admin-muted">Unchecked recipients are inactive for send-now.</p>
                <label className="admin-news-recipient-row" style={{ marginBottom: 2 }}>
                  <input
                    type="checkbox"
                    className="admin-news-recipient-check"
                    checked={recipientsAllSelected}
                    onChange={(e) => this.setAllNewsletterRecipients(e.target.checked)}
                    disabled={recipientKeys.length === 0}
                  />
                  <span className="admin-news-recipient-email">Select / unselect all</span>
                </label>

                <div className="admin-news-recipients-list">
                  {(newsletterRecipients || []).map((item, idx) => {
                    const email = String(item?.email || '').trim().toLowerCase();
                    const checked = email ? newsletterRecipientsSelected[email] !== false : false;
                    return (
                      <label key={`${email || 'row'}-${idx}`} className={`admin-news-recipient-row ${checked ? '' : 'is-inactive'}`}>
                        <input
                          type="checkbox"
                          className="admin-news-recipient-check"
                          checked={checked}
                          onChange={(e) => this.setNewsletterRecipientSelected(email, e.target.checked)}
                        />
                        <span className="admin-news-recipient-email">{item?.email || '-'}</span>
                        <span className="admin-news-recipient-meta">{item?.source || 'newsletter'}</span>
                      </label>
                    );
                  })}
                  {(!newsletterRecipients || newsletterRecipients.length === 0) && (
                    <p className="admin-empty">No recipients loaded yet.</p>
                  )}
                </div>

                <div className="admin-review-modal__actions">
                  <button type="button" className="btn primary" onClick={this.closeNewsletterRecipientsModal}>Done</button>
                </div>
              </div>
            </div>
          </Modal>
            </div>
          </div>
        </main>

        {!isEmbedded ? <Footer /> : null}
        </div>
    );
  }
}
